import trackMetrics from './metrics';
import getEnvelope from './envelope';
import constructData from './constructData';
import sendData from './sendData';
import createRouteObserver from './routeObserver';
import setupPerViewMetrics from './metrics/setupPerViewMetrics';
import WeapiEventPayload from './contracts/weapiEventPayload';
import type { MetricReport } from './contracts/metricReport';
import { prodUrl, devUrl, testUrl } from './constants';

// Meta first, metric fields win on key collision — consumer-supplied
// metadata must NEVER overwrite the metric's own name/value/rating/etc.
const buildCustomAttributes = (
  metric: MetricReport,
  payloadMetaAttributes: object,
) => ({
  ...payloadMetaAttributes,
  ...metric,
});

type TrackPerformanceParams = {
  environment: string;
  project: string;
  track: string;
  customEventName?: string;
  payloadMetaAttributes?: object;
  correlationId?: string;
  perViewMetrics?: boolean;
};

const ANALYTICS_ENDPOINT = {
  production: prodUrl,
  prod: prodUrl,
  development: devUrl,
  dev: devUrl,
  test: testUrl,
};

// Prevent function from being called more than once per session.
let trackPerformanceExecuted = false;

const trackPerformance = ({
  environment,
  project,
  track,
  customEventName = '',
  payloadMetaAttributes = {},
  correlationId = '',
  perViewMetrics = false,
  // eslint-disable-next-line consistent-return
}: TrackPerformanceParams) => {
  if (trackPerformanceExecuted) {
    console.log(
      'trackPerformance() already called. This method should only be called once.',
    );
    return null;
  }
  trackPerformanceExecuted = true;

  if (!environment || !project || !track) {
    throw Error(
      'params environment, project, and track are required for trackPerformance()',
    );
  }

  const endpoint = ANALYTICS_ENDPOINT[environment] || ANALYTICS_ENDPOINT.dev;
  const eventName = customEventName || 'performance';

  if (typeof window !== 'undefined') {
    const send = async (metric: MetricReport, url?: string) => {
      const envelope = await getEnvelope(
        environment,
        project,
        track,
        eventName,
        correlationId,
        url,
      );
      const fullData: WeapiEventPayload = constructData(
        envelope,
        buildCustomAttributes(metric, payloadMetaAttributes),
      );
      // Append metric name as URL query so events are easy to filter in devtools.
      sendData(`${endpoint}?${metric.name}`, fullData);
    };

    if (perViewMetrics) {
      // The route observer monkey-patches history, so it is only installed when per-view tracking is requested; the default path resolves the URL at send time inside getEnvelope.
      setupPerViewMetrics(send, createRouteObserver());
    } else {
      trackMetrics(send);
    }
  }
};

export default trackPerformance;
